import analyticsLoggerImpl from '@autopass-web/mochi-analytics';

import { environment } from '../environments/environment';
import { errorCodeHandler, errorHandler } from '../helper/error-handler';
import { sessionStorageController } from './storages';

const ENTRY = '入口頁';
const SEARCH_PLATE_NUMBER = '車牌數字輸入頁';
const SELECT_PLATE_NUMBER = '車牌選取頁';
const PAYMENT = '付款設定頁';
const SUCCESS = '付款成功頁';
const AUTH = '交易處理頁';
const PROMOTION_LIST = '停車非會員優惠列表頁';
const LEAVE = '會員自行離場提示頁';

export const AMPLITUDE_KEY = environment.amplitudeKey;

class amplitudeService {
  isInit: boolean;

  constructor() {
    this.isInit = false;
  }

  init = (userId?: string) => {
    analyticsLoggerImpl.init(environment.amplitudeKey, userId);
    this.isInit = true;
  };

  setUserId = (userId: string) => {
    if (!this.isInit) this.init();
    analyticsLoggerImpl.setUserId(userId);
  };

  track = (name, properties = {}) => {
    if (!this.isInit) this.init();

    const parkinglot = sessionStorageController.getParkinglot();

    properties['停車場id'] = parkinglot?.id;
    properties['停車場名稱'] = parkinglot?.name;
    analyticsLoggerImpl.log(name, properties);
  };

  // 外部連結開啟
  openFromLink = ({
    url,
    source,
    code,
    name,
  }: {
    url: string;
    source: string;
    code: number | null;
    name: string | null;
  }) => {
    this.track('外部連結_開啟', {
      url,
      source,
      啟動位置代碼: code,
      位置代碼備註: name,
    });
  };

  // 入口頁
  entryPageView = () => this.track(`${ENTRY}_pageview`);
  entryTermsOnClick = () => this.track(`${ENTRY}-服務條款_點擊`);
  entryPrivacyOnClick = () => this.track(`${ENTRY}-隱私權政策_點擊`);
  entryButtonOnClick = () => this.track(`${ENTRY}-開始使用_點擊`);

  // 車牌數字輸入頁
  searchPlateNumberPageView = () =>
    this.track(`${SEARCH_PLATE_NUMBER}_pageview`);
  searchPlateNumberInputOnClick = () =>
    this.track(`${SEARCH_PLATE_NUMBER}-車牌輸入框_點擊`);
  searchPlateNumberHintOnClick = () =>
    this.track(`${SEARCH_PLATE_NUMBER}-車牌輸入提示_點擊`);
  searchPlateNumberHintPageView = () =>
    this.track(`${SEARCH_PLATE_NUMBER}-車牌輸入提示dialog_pageview`);
  searchPlateNumberVehicleTypeOnClick = (type) =>
    this.track(`${SEARCH_PLATE_NUMBER}-車種選項_點擊`, { 車種選項: type });
  searchPlateNumberPromotionOnClick = (id) =>
    this.track(`${SEARCH_PLATE_NUMBER}-廣告_點擊`, {
      版位id: 'aps_promotion',
      廣告: id,
    });
  searchPlateNumberButtonOnClick = (plateNumber, type) =>
    this.track(`${SEARCH_PLATE_NUMBER}-查詢車牌_點擊`, {
      車牌: plateNumber,
      車種: type,
    });
  searchPlateNumberErrorPageView = (error) => {
    if (error.reason) {
      this.track(`${SEARCH_PLATE_NUMBER}-錯誤訊息_pageview`, {
        失敗原因: error.reason,
        error_code: error.code,
      });
    } else {
      this.track(`${SEARCH_PLATE_NUMBER}-錯誤訊息_pageview`, {
        失敗原因: errorHandler(error),
        error_code: errorCodeHandler(error).code,
      });
    }
  };

  // 車牌選取頁
  selectPlateNumberPageView = (isSupportAlpr) =>
    this.track(`${SELECT_PLATE_NUMBER}_pageview`, {
      場站是否提供進場圖片: isSupportAlpr ? 'Y' : 'N',
    });
  selectPlateNumberOnClick = ({
    plateNumber,
    isDisplayImage,
    order,
    ...props
  }: {
    plateNumber: string;
    isDisplayImage: 'Y' | 'N';
    order: number;
  }) => {
    this.track(`${SELECT_PLATE_NUMBER}-車牌搜尋結果_點擊`, {
      車牌號碼: plateNumber,
      是否有顯示照片: isDisplayImage,
      排序順序: order,
      ...props,
    });
  };
  selectPlateNumberAutoPaymentDialogPageView = (plateNumber) =>
    this.track(`${SELECT_PLATE_NUMBER}-會員自動扣款提示dialog_pageview`, {
      車牌號碼: plateNumber,
    });
  selectPlateNumberAutoPaymentDialogOnClick = (plateNumber, action) =>
    this.track(`${SELECT_PLATE_NUMBER}-會員自動扣款提示dialog_點擊`, {
      車牌號碼: plateNumber,
      動作: action,
    });
  selectPlateNumberErrorPageView = (plateNumber, error) =>
    this.track(`${SELECT_PLATE_NUMBER}-錯誤訊息_pageview`, {
      車牌號碼: plateNumber,
      失敗原因: errorHandler(error),
      error_code: errorCodeHandler(error).code,
    });

  // 會員離場頁

  leavePageView = (reason, ticketId = null) =>
    this.track(`${LEAVE}_pageview`, {
      停車憑證id: ticketId,
      離場原因: reason,
    });

  // 付款設定頁

  paymentPageView = ({
    ticketId,
    plateNumber,
  }: {
    ticketId: string;
    plateNumber: string;
  }) =>
    this.track(`${PAYMENT}_pageview`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
    });
  paymentBillingInfoOnClick = ({
    ticketId,
    plateNumber,
  }: {
    ticketId: string;
    plateNumber: string;
  }) =>
    this.track(`${PAYMENT}-計費方式info_點擊`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
    });
  paymentBillingInfoDialogPageView = ({
    ticketId,
    plateNumber,
  }: {
    ticketId: string;
    plateNumber: string;
  }) =>
    this.track(`${PAYMENT}-計費方式dialog_pageview`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
    });
  paymentTimeoutDialogPageView = ({
    ticketId,
    plateNumber,
  }: {
    ticketId: string;
    plateNumber: string;
  }) =>
    this.track(`${PAYMENT}-已超過最晚離場時間dialog_pageview`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
    });
  paymentTicketExpireDialogPageView = () =>
    this.track(`${PAYMENT}-停車憑證過期dialog_pageview`);
  paymentTicketExpireDialogOnClick = () =>
    this.track(`${PAYMENT}-停車憑證過期dialog-重新整理_點擊`);
  paymentDpassSignupSubmit = ({
    ticketId,
    plateNumber,
    email,
    status,
    partnerId,
  }: {
    ticketId: string;
    plateNumber: string;
    email: string;
    status: 'success' | 'failed';
    partnerId?: string;
  }) =>
    this.track(`${PAYMENT}_加入駕駛中心_帳號送出`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
      email,
      帳號送到駕駛中心: status,
      廠商id: partnerId,
    });

  // 付款成功頁
  successPageView = ({
    ticketId,
    plateNumber,
  }: {
    ticketId: string;
    plateNumber: string;
  }) =>
    this.track(`${SUCCESS}_pageview`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
    });

  // 交易處理頁
  authPageView = ({
    ticketId,
    plateNumber,
    paymentType,
  }: {
    ticketId: string;
    plateNumber: string;
    paymentType: string;
  }) =>
    this.track(`${AUTH}_pageview`, {
      停車憑證id: ticketId,
      車牌號碼: plateNumber,
      付款方式: paymentType,
    });

  // 優惠頁
  promotionLeaveOnClick = (item) => {
    const params = item
      ? {
          優惠id: item.id,
        }
      : {};
    this.track(`${PROMOTION_LIST}-前往繳費_點擊`, params);
  };
  successBulletinOnClick = (params: {
    parkinglotId: string;
    parkinglotName: string;
    bulletin_board_code: string;
    bulletin_id: string;
  }) => {
    this.track(`${SUCCESS}-廣告版位_點擊`, params);
  };
  selectCheckoutFlowPageView = (props) => {
    this.track(`車牌選取頁_選擇支付方式 dialog_pageview`, props);
  };
  selectCheckoutActionManual = (props) => {
    this.track(`車牌選取頁_選擇支付方式 dialog_仍手動繳費_點擊`, props);
  };
  selectCheckoutActionAlpr = (props) => {
    this.track(`車牌選取頁_選擇支付方式 dialog_車辨付款_點擊`, props);
  };
}

export default new amplitudeService();
